/*
__/\\\\\\\\\\\\\\\__/\\\\\\\\\\\\\\\_____/\\\\\\\\\____        
 _\///////\\\/////__\///////\\\/////____/\\\\\\\\\\\\\__       
  _______\/\\\_____________\/\\\________/\\\/////////\\\_      
   _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_     
    _______\/\\\_____________\/\\\_______\/\\\\\\\\\\\\\\\_    
     _______\/\\\_____________\/\\\_______\/\\\/////////\\\_   
      _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_  
       _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_ 
        _______\///______________\///________\///________\///__
            
            COPYRIGHT TACTICAL TRANSPORTATION ADVISORS, INC. 
            ALL RIGHTS RESERVED.
*/

import { useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import ControlStateProps from "../../../../models/State/ControlStateProps";
import { Validation } from "../../../../validation";
import moment from "moment";
import SubscriberWrapper from "../../../../components/SubscriberWrapper";
import DocumentWizard from "../../DocumentWizard";
import DocumentSignatureButton from "../../DocumentSignatureButton";
import DocumentField from "../../DocumentField";
import DocumentRadioCheck from "../../DocumentRadioCheck";
import DocumentCheckbox from "../../DocumentCheckbox";
import { submitDocument } from "../../../../services/EmployeeOnboardingService";
import W4Pdf from "./W4Pdf";
import { reactPdfToBase64 } from "../../../../tools";
import { AppGlobals } from "../../../../App";
import W4 from "./W4";
import W42025Page1 from "../../../../../src/assets/W-4/2025/W-4-1.png";
import W42025Page2 from "../../../../../src/assets/W-4/2025/W-4-2.png";
import W42025Page3 from "../../../../../src/assets/W-4/2025/W-4-3.png";
import W42025Page4 from "../../../../../src/assets/W-4/2025/W-4-4.png";
import W42026Page1 from "../../../../../src/assets/W-4/2026/W-4-1.png";
import W42026Page2 from "../../../../../src/assets/W-4/2026/W-4-2.png";
import W42026Page3 from "../../../../../src/assets/W-4/2026/W-4-3.png";
import W42026Page4 from "../../../../../src/assets/W-4/2026/W-4-4.png";
import W42026Page5 from "../../../../../src/assets/W-4/2026/W-4-5.png";

export default function W4Form({ document, userData, companyData, startDate, signature, onboardingToken, hideModal, updateDocumentToCompleted }) {
  const [w4, _] = useState(W4.initFromData(document, userData, companyData, startDate));
  const [zoom, setZoom] = useState(1);
  const fieldRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  const handleSubmit = async () => {
    const pdf = <W4Pdf signature={signature} w4={w4} />;
    const base64 = await reactPdfToBase64(pdf);
    if (base64) {
      const response = await submitDocument(w4.encode(document, base64), onboardingToken);
      if (response.status === 200) {
        updateDocumentToCompleted("w4", base64);
        hideModal();
        AppGlobals.alert("success", response.message);
      } else {
        AppGlobals.alert("danger", response.message);
      }
    }
  };

  //////////////////////////
  /// Version Positioning
  //////////////////////////

  /// 2025
  const positions2025 = {
    has2Jobs: {
      x: 782,
      y: 535,
    },
    step3Children: {
      x: 570,
      y: 619,
      width: 90,
    },
    step3OtherDependents: {
      x: 570,
      y: 645,
      width: 90,
    },
    step3Total: {
      x: 710,
      y: 685,
      width: 90,
    },
    step4OtherIncome: {
      x: 710,
      y: 736,
      width: 90,
    },
    step4Deductions: {
      x: 710,
      y: 794,
      width: 90,
    },
    step4ExtraWitholding: {
      x: 710,
      y: 827,
      width: 90,
    },
    step5Signed: {
      x: 134,
      y: 910,
      width: 105,
      height: 32,
    },
    signedDate: {
      x: 650,
      y: 917,
    },
  };

  /// 2026
  const positions2026 = {
    has2Jobs: {
      x: 782,
      y: 559,
    },
    step3Children: {
      x: 578,
      y: 661,
      width: 90,
      height: 14,
    },
    step3OtherDependents: {
      x: 578,
      y: 682,
      width: 90,
      height: 14,
    },
    step3Total: {
      x: 710,
      y: 712,
      width: 90,
    },
    step4OtherIncome: {
      x: 710,
      y: 761,
      width: 90,
    },
    step4Deductions: {
      x: 710,
      y: 811,
      width: 90,
    },
    step4ExtraWitholding: {
      x: 710,
      y: 838,
      width: 90,
    },
    step5Signed: {
      x: 134,
      y: 943,
      width: 105,
      height: 32,
    },
    signedDate: {
      x: 650,
      y: 950,
    },
  };

  const year = 2026 || w4.year;
  const positions = year == 2025 ? positions2025 : positions2026;
  const pages = {
    one: year === 2025 ? W42025Page1 : W42026Page1,
    two: year === 2025 ? W42025Page2 : W42026Page2,
    three: year === 2025 ? W42025Page3 : W42026Page3,
    four: year === 2025 ? W42025Page4 : W42026Page4,
    five: year === 2026 ? W42026Page5 : null,
  };

  return (
    <>
      <Modal.Header closeButton />
      <Modal.Body style={{ padding: 0, overflowX: "scroll", backgroundColor: "lightgray" }}>
        <div style={{ transform: `scale(${zoom})`, display: "flex", flexDirection: "column", justifyContent: "center", gap: 12, height: (1100 * (year === 2025 ? 4 : 5) + 12 * 3 + 16) * zoom }}>
          <div style={{ position: "relative", marginLeft: "auto", marginRight: "auto" }}>
            <img src={pages.one} style={{ width: 850, height: 1100 }} />
            <DocumentField innerRef={fieldRefs[0]} stateProps={new ControlStateProps("firstNameMiddleInitial", w4, Validation.nonEmptyString)} maxLength={20} x={134} y={128} width={240} />
            <DocumentField innerRef={fieldRefs[1]} stateProps={new ControlStateProps("lastName", w4, Validation.nonEmptyString)} maxLength={20} x={385} y={128} width={270} />
            <DocumentField innerRef={fieldRefs[2]} stateProps={new ControlStateProps("ssn", w4, Validation.ssn)} x={666} y={128} width={128} maxLength={9} type="number" />
            <DocumentField innerRef={fieldRefs[3]} stateProps={new ControlStateProps("address", w4, Validation.nonEmptyString)} maxLength={30} x={134} y={162} width={400} />
            <DocumentField innerRef={fieldRefs[4]} stateProps={new ControlStateProps("cityStateZip", w4, Validation.nonEmptyString)} maxLength={50} x={134} y={195} width={400} />

            <DocumentRadioCheck innerRef={fieldRefs[5]} value={0} stateProps={new ControlStateProps("filingStatus", w4, Validation.notUndefined, "filingStatus0")} x={159} y={219} />
            <DocumentRadioCheck innerRef={fieldRefs[6]} value={1} stateProps={new ControlStateProps("filingStatus", w4, Validation.notUndefined, "filingStatus1")} x={159} y={235} />
            <DocumentRadioCheck innerRef={fieldRefs[7]} value={2} stateProps={new ControlStateProps("filingStatus", w4, Validation.notUndefined, "filingStatus2")} x={159} y={251} />

            <DocumentCheckbox innerRef={fieldRefs[8]} value={2} stateProps={new ControlStateProps("has2Jobs", w4)} {...positions.has2Jobs} />

            <DocumentField type="number" minDecimal={0} maxDecimal={999999.99} innerRef={fieldRefs[9]} stateProps={new ControlStateProps("step3Children", w4)} {...positions.step3Children} />
            <DocumentField
              type="number"
              minDecimal={0}
              maxDecimal={999999.99}
              innerRef={fieldRefs[10]}
              stateProps={new ControlStateProps("step3OtherDependents", w4)}
              {...positions.step3OtherDependents}
            />
            <DocumentField type="number" minDecimal={0} maxDecimal={999999.99} innerRef={fieldRefs[11]} stateProps={new ControlStateProps("step3Total", w4)} {...positions.step3Total} />

            <DocumentField type="number" minDecimal={0} maxDecimal={999999.99} innerRef={fieldRefs[12]} stateProps={new ControlStateProps("step4OtherIncome", w4)} {...positions.step4OtherIncome} />
            <DocumentField type="number" minDecimal={0} maxDecimal={999999.99} innerRef={fieldRefs[13]} stateProps={new ControlStateProps("step4Deductions", w4)} {...positions.step4Deductions} />
            <DocumentField
              type="number"
              minDecimal={0}
              maxDecimal={9999.99}
              innerRef={fieldRefs[14]}
              stateProps={new ControlStateProps("step4ExtraWitholding", w4)}
              {...positions.step4ExtraWitholding}
            />

            {year === 2026 && <DocumentCheckbox innerRef={fieldRefs[15]} stateProps={new ControlStateProps("claimExemption", w4)} x={782} y={907} />}

            <DocumentSignatureButton innerRef={fieldRefs[16]} signature={signature} stateProps={new ControlStateProps("step5Signed", w4, Validation.true)} {...positions.step5Signed} />
            <SubscriberWrapper
              stateProps={new ControlStateProps("step5Signed", w4, null, "wrapper for date")}
              render={() => {
                return !w4.step5Signed ? "" : <span style={{ position: "absolute", left: positions.signedDate.x, top: positions.signedDate.y }}>{moment().format("MM/DD/YYYY")}</span>;
              }}
            />
          </div>
          <div style={{ position: "relative", marginLeft: "auto", marginRight: "auto" }}>
            <img src={pages.two} style={{ width: 850, height: 1100 }} />
          </div>
          <div style={{ position: "relative", marginLeft: "auto", marginRight: "auto" }}>
            <img src={pages.three} style={{ width: 850, height: 1100 }} />
          </div>
          <div style={{ position: "relative", marginLeft: "auto", marginRight: "auto" }}>
            <img src={pages.four} style={{ width: 850, height: 1100 }} />
          </div>
          {pages.five && (
            <div className="relative mx-auto">
              <img src={pages.five} style={{ width: 850, height: 1100, maxWidth: 10000 }} />
            </div>
          )}
        </div>
      </Modal.Body>
      <DocumentWizard document={w4} fieldRefs={fieldRefs} refCount={17} zoom={zoom} setZoom={setZoom} onSubmit={handleSubmit} />
    </>
  );
}
